import { HandleCallbackActionParams } from '@domain-group/javascript-utils'
import { Dispatch, SetStateAction, useState } from 'react'

import useQuoteBuilder from '@components/QuoteBuilder/components/CampaignQuoteBuilder/hooks/useQuoteBuilder'
import useCampaignQuoteSections from '@hooks/useCampaignQuoteSections'

import type { CampaignModel, CampaignQuoteItemModel, CampaignQuoteModel } from '@models/types'

type CampaignQuoteItemWithDate = {
  id: number,
  required_by: string | null,
}

export type ModalState = {
  allItemsRequiredByToday: boolean,
  campaignQuoteItems: Record<number, CampaignQuoteItemWithDate>,
}

export type SetModalState = Dispatch<SetStateAction<ModalState>>

type HandleUpdateAllItemsRequiredByTodayParams = {
  initialState: Record<number, CampaignQuoteItemWithDate>,
  setState: SetModalState,
  state: ModalState,
}

const handleUpdateAllItemsRequiredByToday = (params: HandleUpdateAllItemsRequiredByTodayParams) => {
  const { initialState, setState, state } = params

  const { allItemsRequiredByToday } = state

  const newToggleValue = !allItemsRequiredByToday
  const today = new Date().toISOString().split('T')[0]

  setState((prevState) => {
    const updatedItems = Object.fromEntries(
      Object.entries(prevState.campaignQuoteItems).map(([id, item]) => [
        id,
        { ...item, required_by: newToggleValue ? today : initialState[Number(id)].required_by },
      ]),
    )

    return {
      ...prevState,
      allItemsRequiredByToday: newToggleValue,
      campaignQuoteItems: updatedItems,
    }
  })
}

type UseCampaignQuoteItemDatesModalParams = {
  campaignQuote: CampaignQuoteModel,
  filteredCampaignQuoteItems: CampaignQuoteItemModel[],
}

export default function useCampaignQuoteItemDatesModal(params: UseCampaignQuoteItemDatesModalParams) {
  const { campaignQuote, filteredCampaignQuoteItems } = params
  const initialState = filteredCampaignQuoteItems.reduce((acc, item) => {
    const initialRequiredByDate = item.required_by || ''
    // @ts-expect-error TS7053
    acc[item.id] = { id: item.id, required_by: initialRequiredByDate }
    return acc
  }, {})

  const defaultState: ModalState = {
    allItemsRequiredByToday: false,
    campaignQuoteItems: initialState,
  }
  const [state, setState] = useState(defaultState)

  const quoteBuilderPayload = useQuoteBuilder({
    campaign: { id: campaignQuote.campaign_id } as CampaignModel,
    campaignQuoteId: campaignQuote.id,
  })

  const { sortedSectionItemIds } = quoteBuilderPayload

  const campaignQuoteSectionsPayload = useCampaignQuoteSections({
    filters: { campaign_quote_id: campaignQuote.id },
  })
  const { filteredCampaignQuoteSections } = campaignQuoteSectionsPayload

  return {
    callbacks: {
      handleUpdateAllItemsRequiredByToday: () => {
        handleUpdateAllItemsRequiredByToday({
          initialState,
          setState,
          state,
        })
      },
    },
    filteredCampaignQuoteSections,
    setState,
    state,
    sortedSectionItemIds,
  }
}
